import React, { useRef } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { matchPath, Navigate, Route, useLocation, Routes } from 'react-router';
import { Box } from '@mui/material';
import moment from 'moment';
import smartlookClient from 'smartlook-client';
import { DESKTOP_COLORS } from 'src/consts';
import { useQuery } from 'src/hooks/useQuery';
import { useResponsive } from 'src/hooks/useResponsive';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import { makeRouteScenario } from 'src/utils/route';
import { HttpClient } from 'components/http/HttpClient';
import { OrderScenario } from 'components/order/model/Order';
import { getDateFormatByCountry, localConfig } from 'components/timeslots/helpers';
import {
    getLocalAuthentication,
    resetLocalAuthentication,
    setOnCloseRedirectUrl
} from 'components/user/localAuth';
import { CONTENT_MAX_WIDTH, REDUCED_CONTENT_MAX_WIDTH } from 'config/constants';
import i18n, { loadTranslations } from 'config/i18n';
import { ConfirmDialog } from 'lib/ConfirmDialog';
import logger from 'lib/logger';
import { GlobalLayout } from 'lib/GlobalLayout';
import { Throbber } from 'lib/Throbber';
import { isObjectId } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { UserLayout } from 'lib/UserLayout';
import { EmailCapturePage } from 'pages/email-capture';
import { TwoFactorAuthenticationPage } from 'pages/two-factor-authentication';
import {
    createGuestSession,
    getUserDetails,
    resetReturnUrl,
    setShowMarketing,
    updateUser
} from 'store/auth/authActions';
import { getLayout } from 'store/layout/layoutActions';
import { getCurrentLocation } from 'store/locations/locationActions';
import { checkLoadingImage, loadSettings } from 'store/settings/settingsActions';
import { ApplicationState } from 'store/store';
import { PayOperationStatusPage } from './pay/pay-operation';
import { PaySummaryPage } from './pay/summary';
import { ActivatePage } from './activate';
import { ActivityPage } from './activity';
import { AwardsPage } from './awards';
import { Basket } from './basket';
import { ForgotPasswordPage } from './forgot-password';
import { LocationsPage } from './locations';
import { MenuPage } from './menu';
import { OperationStatusPage } from './operation-status';
import { OrderEntryPage } from './order-entry';
import { OrderSummaryPage } from './order-summary';
import { PayPage } from './pay';
import { PaymentMethodsPage } from './payment-methods';
import { ROUTES, RELATIVE_ROUTES } from './routes';
import { SignInPage } from './signin';
import { SignUpPage } from './signup';
import { UserInfo } from './user-info';
import { Helmet as ReactHelmet, HelmetProps as ReactHelmetProps } from 'react-helmet';
import { getGoogleTagsManagerScript } from 'lib/googleHelpers';
import { UserDataFormData, UserMissingDataScreen } from 'components/user/UserMissingDataScreen';
import { BottomDialog } from 'lib/BottomDialog';
import { userApi } from 'components/user/userApi';
import { isUserMissingName } from 'components/user/model/User';
import { SWEETWATERS_CONTAINER, SWEETWATERS_ID, TORTILLA_CONTAINER, TORTILLA_ID } from 'lib/useGAHelpers';
import { CookieConsentDialog, getConsentValue, hasConsentTo } from 'app/CookieConsentDialog';
import { PayBasket } from './pay/pay-basket';
import { JourneyLandingPage } from './journey';

const PREFIX = 'BasePage';

const classes = {
    rootWrapper: `${PREFIX}rootWrapper`,
    rootBox: `${PREFIX}rootBox`
};

const StyledBox = styled(Box, {
    shouldForwardProp: prop =>
        !['backgroundImg', 'isDesktop', 'isWhiteBackgroundOnDesktop', 'isReducedWidth'].includes(
            prop as string
        )
})<{
    backgroundImg: string;
    isDesktop: boolean;
    isWhiteBackgroundOnDesktop: boolean;
    isReducedWidth?: boolean;
}>(({ theme, backgroundImg, isDesktop, isWhiteBackgroundOnDesktop, isReducedWidth }) => ({
    height: '100%',
    [`& .${classes.rootWrapper}`]: {
        height: '100%',
        overflowY: 'auto',
        ...(isDesktop &&
            (!!backgroundImg
                ? {
                      backgroundImage: `url(${backgroundImg})`,
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center center'
                  }
                : {
                      backgroundColor: DESKTOP_COLORS.BACKGROUND_GREY
                  })),
        ...(!backgroundImg &&
            isDesktop &&
            isWhiteBackgroundOnDesktop && {
                backgroundColor: theme.palette.common.white
            })
    },

    [`& .${classes.rootBox}`]: {
        width: '100%',
        height: '100%',
        maxWidth: `${isReducedWidth ? REDUCED_CONTENT_MAX_WIDTH : CONTENT_MAX_WIDTH}px`,
        margin: '0 auto'
    }
}));

interface HelmetProps extends ReactHelmetProps, React.PropsWithChildren {}

const Helmet = (props: HelmetProps) => <ReactHelmet {...props} />;

const PreoderRedirect = () => {
    const { getParsedPath } = useLocalHistory();
    const path = getParsedPath(ROUTES.MENU, {
        scenario: makeRouteScenario(OrderScenario.PREORDER)
    });
    return <Navigate to={path} replace />;
};

const RedirectToBase = () => <Navigate to={ROUTES.BASE} replace />;

const QuickPayCheckoutRedirect = () => {
    const { getParsedPath } = useLocalHistory();
    return (
        <Navigate
            to={getParsedPath(ROUTES.QUICKPAY.CHECKOUT, {
                scenario: makeRouteScenario(OrderScenario.TABLE)
            })}
            replace
        />
    );
};

// to prevent making get location with locationId=sign-in
const stopWords = ['user', 'status', 'order'];

const whiteBackgroundRoutes = [ROUTES.BASE];

const QUICKPAY_URLS = [ROUTES.QUICKPAY.CHECKOUT, ROUTES.QUICKPAY.STATUS, ROUTES.QUICKPAY.SUMMARY];
const AUTHORIZATION_URLS = [
    ROUTES.USER.LOGIN,
    ROUTES.USER.REGISTER,
    ROUTES.USER.ACTIVATE,
    ROUTES.USER.FORGOT_PASSWORD,
    ROUTES.USER.TWO_FACTOR_AUTHENTICATION
];

export const BasePage: React.FC = () => {
    const [isSessionExpired, setIsSessionExpired] = React.useState(false);
    const [scenario] = useRouteScenario();
    // const { merchantId, locationId } = useParams<LocationRouteParams>();
    const { push, getParsedPath } = useLocalHistory();
    const queryLocationId = useQuery('locationId');
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { user, isGuest, showMarketing } = useAuth();
    const { pathname } = useLocation();
    // Hack to get merchantId and locationId from the URL
    const pathSegments = pathname.split('/').filter(Boolean);
    const [merchantId, locationId] = pathSegments;
    const { hasSession } = useAuth();
    const { isDesktop } = useResponsive();
    const [isSmartlookSet, setIsSmartlookSet] = React.useState(false);
    const [consentVerified, setConsentVerified] = React.useState(false);
    const isQuickpayUrl = React.useMemo(
        () => QUICKPAY_URLS.some(item => matchPath(pathname, item)),
        [pathname]
    );
    const isV2Url = isQuickpayUrl;
    const isAuthroizationUrl = React.useMemo(
        () => AUTHORIZATION_URLS.some(item => matchPath(pathname, item)),
        [pathname]
    );
    const isWhiteBackgroundOnDesktop = React.useMemo(
        () =>
            whiteBackgroundRoutes.some(
                path =>
                    !!matchPath(
                        {
                            path
                        },
                        pathname
                    )
            ),
        [pathname]
    );
    // Do we want to scrap all the desktop views?
    const isReducedWidth = React.useMemo(
        () =>
            !!matchPath(
                {
                    path: ROUTES.USER.REWARDS
                },
                pathname
            ),
        [pathname]
    );
    const backgroundImageProps = React.useMemo(
        () => ({
            backgroundImg: `${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/web_desktop_background`,
            isDesktop,
            isWhiteBackgroundOnDesktop,
            isReducedWidth
        }),
        [merchantId, isDesktop, isWhiteBackgroundOnDesktop, isReducedWidth]
    );
    const { isLoading, settings, isInitiallyLoaded } = useSelector(
        (state: ApplicationState) => state.settings,
        (prevState, nextState) =>
            shallowEqual(prevState.settings, nextState.settings) &&
            prevState.isLoading === nextState.isLoading
    );
    const {
        isLoading: isLayoutLoading,
        tenantId: layoutTenantId,
        layout
    } = useSelector(
        (state: ApplicationState) => state.layout,
        (prevState, nextState) => prevState.isLoading === nextState.isLoading
    );
    const { currentLocation } = useSelector((state: ApplicationState) => state.locations);
    const correctLocationId = React.useMemo(
        () =>
            (isObjectId(locationId) && locationId) ||
            (isObjectId(queryLocationId) && queryLocationId) ||
            'common',
        [locationId, queryLocationId]
    );
    const isValidSetting = React.useMemo(
        () =>
            correctLocationId !== 'common'
                ? settings?.locationId === correctLocationId
                : settings?.locationId === 'common',
        [correctLocationId, settings?.locationId]
    );

    React.useEffect(() => {
        if (
            settings?.smartlookEnabled &&
            process.env.SMARTLOOK_API_KEY &&
            !isSmartlookSet &&
            consentVerified &&
            merchantId
        ) {
            setIsSmartlookSet(true);
            const consentState = getConsentValue(merchantId);
            smartlookClient.init(process.env.SMARTLOOK_API_KEY, {
                cookies: !!consentState && hasConsentTo(consentState, 'analytics')
            });
        }
    }, [consentVerified, isSmartlookSet, merchantId, settings?.smartlookEnabled]);

    React.useEffect(() => {
        if (isObjectId(merchantId) && !!settings?.applicationId) {
            const favicon = document.getElementById('favicon') as HTMLLinkElement;
            const appleIcon = document.getElementById('apple-touch-icon') as HTMLLinkElement;
            checkLoadingImage()(dispatch);
            if (favicon) {
                // We are replacing favicon.href to use merchant based icon
                favicon.href = `${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/icon_40`;
            }
            if (appleIcon) {
                // We are replacing appleIcon.href to use merchant based icon (safari specific)
                appleIcon.href = `${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/icon_40`;
            }
            if (!i18n.languages.includes(merchantId)) {
                loadTranslations(merchantId);
            }
        }
    }, [merchantId, settings?.applicationId, dispatch]);

    const handleUnauthorized = React.useCallback(() => {
        if (!isAuthroizationUrl) {
            if (!isGuest) {
                setIsSessionExpired(true);
            } else {
                createGuestSession(dispatch);
            }
        }
    }, [dispatch, isAuthroizationUrl, isGuest]);
    // To prevent event to be added multiple times
    const authorizationEventHandler = useRef(handleUnauthorized);

    React.useEffect(() => {
        HttpClient.events.removeListener('unauthorized', authorizationEventHandler.current);
        HttpClient.events.addListener('unauthorized', handleUnauthorized);
        authorizationEventHandler.current = handleUnauthorized;

        return () => {
            HttpClient.events.removeListener('unauthorized', handleUnauthorized);
        };
    }, [handleUnauthorized]);

    // Reset Login / Sign Up Redirect
    React.useEffect(() => resetReturnUrl(dispatch), [dispatch]);
    React.useEffect(() => {
        if (!user && settings && !isAuthroizationUrl) {
            const localAuthentication = getLocalAuthentication();

            if (localAuthentication) {
                getUserDetails(localAuthentication.userId, true)(dispatch);
            } else {
                createGuestSession(dispatch);
            }
        }
    }, [dispatch, isAuthroizationUrl, settings, user]);

    React.useEffect(() => {
        if (settings) {
            const dateFormat = getDateFormatByCountry(settings.region.code);
            moment.locale(settings.region.code || 'en', {
                ...localConfig,
                calendar: {
                    ...localConfig.calendar,
                    nextWeek: `dddd [at] ${dateFormat}`,
                    sameElse: dateFormat
                }
            });
        }
    }, [settings]);

    React.useEffect(() => {
        if (merchantId) {
            if (!isInitiallyLoaded || settings?.locationId !== correctLocationId) {
                if (isAuthroizationUrl && !!settings) {
                    return;
                }
                loadSettings(
                    merchantId,
                    correctLocationId
                )(dispatch).then(() => {
                    if (!layout || layoutTenantId !== merchantId) {
                        getLayout()(dispatch);
                    }
                });
            }
        }
    }, [
        merchantId,
        dispatch,
        locationId,
        isInitiallyLoaded,
        settings?.locationId,
        queryLocationId,
        layout,
        layoutTenantId,
        isAuthroizationUrl,
        settings,
        isGuest,
        settings?.region?.code,
        correctLocationId
    ]);

    React.useEffect(() => {
        if (
            !!locationId &&
            isObjectId(locationId) &&
            !stopWords.includes(locationId.toLowerCase()) &&
            !!settings?.locationId &&
            (!currentLocation || currentLocation?._id !== locationId)
        ) {
            getCurrentLocation(locationId)(dispatch);
        }
    }, [locationId, dispatch, settings?.locationId, currentLocation, currentLocation?._id]);

    const handleConfirm = React.useCallback(() => {
        if (!!merchantId) {
            if (!!locationId && !!scenario) {
                if ([OrderScenario.ORDER_TO_TABLE, OrderScenario.TABLE].includes(scenario)) {
                    setOnCloseRedirectUrl(getParsedPath(ROUTES.JOURNEY.LANDING), merchantId);
                } else {
                    setOnCloseRedirectUrl(getParsedPath(ROUTES.MENU, {}), merchantId);
                }
            } else {
                setOnCloseRedirectUrl(getParsedPath(ROUTES.BASE, { merchantId, locationId: '' }), merchantId);
            }
        }
        push(ROUTES.USER.LOGIN);
        resetLocalAuthentication();
        setIsSessionExpired(false);
        resetLocalAuthentication();
        dispatch(updateUser(undefined));
    }, [merchantId, push, dispatch, locationId, scenario, getParsedPath]);

    const getMerchantSpecificHead = React.useCallback(() => {
        switch (merchantId) {
            case TORTILLA_ID:
                return (
                    <script type="text/javascript">{getGoogleTagsManagerScript(TORTILLA_CONTAINER)}</script>
                );
            case SWEETWATERS_ID:
                return (
                    <script type="text/javascript">
                        {getGoogleTagsManagerScript(SWEETWATERS_CONTAINER)}
                    </script>
                );
            default:
                return null;
        }
    }, [merchantId]);

    const showDataCollectionScreen = React.useMemo(() => {
        if (user && !isGuest) {
            if (
                isUserMissingName(user) ||
                (settings?.app?.ageRequestEnabled && !user.birthdate) ||
                showMarketing
            ) {
                return true;
            }
        }
        return false;
    }, [isGuest, settings?.app?.ageRequestEnabled, showMarketing, user]);

    const handleSubmitData = React.useCallback(
        async (data: UserDataFormData) => {
            try {
                if (user?._id) {
                    const result = await userApi.updateUser(user?._id, data);
                    dispatch(updateUser(result));
                }
            } catch (e: any) {
                logger.error(e.message, e);
            } finally {
                dispatch(setShowMarketing(false));
            }
        },
        [dispatch, user?._id]
    );

    const handleConsentVerified = React.useCallback(() => {
        setConsentVerified(true);
    }, []);

    if (
        !isV2Url &&
        (isLoading ||
            isLayoutLoading ||
            (!hasSession && !isAuthroizationUrl && !isSessionExpired) ||
            (!isValidSetting && !isAuthroizationUrl) ||
            (!hasSession && !isAuthroizationUrl))
    ) {
        return (
            <>
                <Throbber text={t('GENERAL_LOADING')} />
                <ConfirmDialog
                    open={isSessionExpired && !isAuthroizationUrl}
                    disableBackdropClick
                    disableEscapeKeyDown
                    onClose={handleConfirm}
                    confirmMessage={t('OK')}
                    title={t('DIALOG_OOPS')}
                    onConfirm={handleConfirm}
                    description={t('DIALOG_USER_SESSION_INVALID')}
                    isCancellable={false}
                />
            </>
        );
    }
    if (!isV2Url && !settings) {
        return t('GENERAL_CANNOT_LOAD', { instance: t('MANIFEST') });
    }

    return (
        <StyledBox {...backgroundImageProps}>
            <Box className={classes.rootWrapper}>
                <Helmet>{process.env.NODE_ENV === 'production' && getMerchantSpecificHead()}</Helmet>
                <Box className={classes.rootBox}>
                    <Routes>
                        <Route
                            element={
                                <GlobalLayout>
                                    <OrderEntryPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.ORDER.ENTRY}
                        />
                        <Route
                            element={
                                <GlobalLayout>
                                    <LocationsPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.BASE}
                        />
                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <SignInPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.USER.LOGIN}
                        />
                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <TwoFactorAuthenticationPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.USER.TWO_FACTOR_AUTHENTICATION}
                        />
                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <ActivityPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.USER.ACTIVITY}
                        />
                        <Route
                            element={
                                <GlobalLayout useBackground fullHeight>
                                    <PaymentMethodsPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.USER.PAYMENT_METHODS}
                        />
                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <UserLayout>
                                        <UserInfo />
                                    </UserLayout>
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.USER.DETAILS}
                        />

                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <UserLayout>
                                        <AwardsPage />
                                    </UserLayout>
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.USER.REWARDS}
                        />
                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <SignUpPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.USER.REGISTER}
                        />
                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <ActivatePage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.USER.ACTIVATE}
                        />

                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <EmailCapturePage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.USER.EMAIL_CAPTURE}
                        />
                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <ForgotPasswordPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.USER.FORGOT_PASSWORD}
                        />

                        <Route
                            element={
                                <GlobalLayout>
                                    <JourneyLandingPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.JOURNEY.LANDING}
                        />
                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <OperationStatusPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.ORDER.OPERATION}
                        />

                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <OrderSummaryPage />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.ORDER.SUMMARY}
                        />

                        <Route
                            element={
                                <GlobalLayout useBackground fullHeight>
                                    <Basket />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.ORDER.BASKET}
                        />

                        <Route element={<PayBasket />} path={RELATIVE_ROUTES.QUICKPAY.BASKET} />
                        <Route element={<PayPage />} path={RELATIVE_ROUTES.QUICKPAY.CHECKOUT} />
                        <Route element={<PayOperationStatusPage />} path={RELATIVE_ROUTES.QUICKPAY.STATUS} />
                        <Route element={<PaySummaryPage />} path={RELATIVE_ROUTES.QUICKPAY.SUMMARY} />
                        <Route
                            element={
                                <GlobalLayout useBackground>
                                    <QuickPayCheckoutRedirect />
                                </GlobalLayout>
                            }
                            path={RELATIVE_ROUTES.JOURNEY.PAT.BILL}
                        />
                        <Route
                            element={<QuickPayCheckoutRedirect />}
                            path={RELATIVE_ROUTES.JOURNEY.PAT.BILL_WITHOUT_SCENARIO}
                        />
                        <Route element={<PreoderRedirect />} path={RELATIVE_ROUTES.MENU_WITHOUT_SCENARIO} />
                        <Route element={<MenuPage />} path={RELATIVE_ROUTES.MENU} />
                        <Route element={<RedirectToBase />} path="*" />
                    </Routes>
                    <BottomDialog open={showDataCollectionScreen}>
                        <UserMissingDataScreen
                            onSubmit={handleSubmitData}
                            birthdateEnabled={settings?.app?.ageRequestEnabled}
                            birthdateRequired={(settings?.app?.registrationMinimumAge ?? 0) > 0}
                            user={user}
                        />
                    </BottomDialog>
                    <ConfirmDialog
                        open={isSessionExpired && !isAuthroizationUrl}
                        disableEscapeKeyDown
                        onClose={handleConfirm}
                        confirmMessage={t('OK')}
                        title={t('DIALOG_OOPS')}
                        onConfirm={handleConfirm}
                        description={t('DIALOG_USER_SESSION_INVALID')}
                        isCancellable={false}
                    />
                </Box>
            </Box>
            <CookieConsentDialog onVerify={handleConsentVerified} />
        </StyledBox>
    );
};
